import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PlusIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { getClaims } from '../services/insuranceService';
import { getClients } from '../services/clientService';
import Badge from '../components/common/Badge';
import EmptyState from '../components/common/EmptyState';
import TableSkeleton from '../components/common/skeletons/TableSkeleton';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'denied', label: 'Denied' },
  { value: 'partial', label: 'Partial' },
];

const STATUS_VARIANTS = {
  draft: 'default',
  submitted: 'info',
  pending: 'warning',
  paid: 'success',
  denied: 'error',
  partial: 'warning',
};

const fmt = (val) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val ?? 0);

function StatCard({ label, value, accent }) {
  return (
    <div className="card">
      <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${accent || 'text-neutral-900 dark:text-neutral-100'}`}>{value}</p>
    </div>
  );
}

export default function Insurance() {
  const [clientId, setClientId] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const { data: claimsData, isLoading, isError } = useQuery({
    queryKey: ['claims', { clientId, status, page }],
    queryFn: () => getClaims({ clientId: clientId || undefined, status: status || undefined, page, limit: 25 }),
    keepPreviousData: true,
  });

  const { data: clientsData } = useQuery({
    queryKey: ['clients', { limit: 200 }],
    queryFn: () => getClients({ limit: 200 }),
  });

  const claims = claimsData?.claims ?? [];
  const pagination = claimsData?.pagination;
  const clients = clientsData?.clients ?? [];

  const totalBilled = claims.reduce((s, c) => s + parseFloat(c.billedAmount || 0), 0);
  const totalPaid = claims.reduce((s, c) => s + parseFloat(c.paidAmount || 0), 0);
  const totalPending = claims.filter((c) => ['submitted', 'pending'].includes(c.status))
    .reduce((s, c) => s + parseFloat(c.billedAmount || 0), 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Insurance</h1>
        <div className="flex gap-2">
          <Link to="/insurance/eras" className="btn btn-secondary text-sm">ERA Log</Link>
          <Link to="/insurance/new" className="btn btn-primary flex items-center gap-2">
            <PlusIcon className="h-4 w-4" />
            New claim
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <StatCard label="Total billed (page)" value={fmt(totalBilled)} />
        <StatCard label="Total paid (page)" value={fmt(totalPaid)} accent="text-green-600 dark:text-green-400" />
        <StatCard label="Pending / submitted (page)" value={fmt(totalPending)} accent={totalPending > 0 ? 'text-yellow-600 dark:text-yellow-400' : 'text-neutral-900 dark:text-neutral-100'} />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <select
          value={clientId}
          onChange={(e) => { setClientId(e.target.value); setPage(1); }}
          className="input w-full sm:w-52"
        >
          <option value="">All clients</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.lastName}, {c.firstName}</option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="input w-full sm:w-44"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <TableSkeleton rows={6} />
      ) : isError ? (
        <div className="text-center py-20 text-danger-600 dark:text-danger-400">Failed to load claims.</div>
      ) : claims.length === 0 ? (
        <EmptyState
          icon={ShieldCheckIcon}
          title={clientId || status ? 'No claims match your filters' : 'No claims yet'}
          description="Create a claim to start tracking insurance submissions."
          action={!clientId && !status && (
            <Link to="/insurance/new" className="btn btn-primary">New claim</Link>
          )}
        />
      ) : (
        <>
          <div className="card p-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-700">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Payer</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Client</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Claim #</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Submitted</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Billed</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Paid</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {claims.map((claim) => (
                  <tr key={claim.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors">
                    <td className="px-6 py-4">
                      <Link
                        to={`/insurance/${claim.id}`}
                        className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
                      >
                        {claim.payerName}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-neutral-600 dark:text-neutral-400">
                      {claim.client ? `${claim.client.firstName} ${claim.client.lastName}` : '—'}
                    </td>
                    <td className="px-6 py-4 text-neutral-500 dark:text-neutral-400">{claim.claimNumber || '—'}</td>
                    <td className="px-6 py-4 text-neutral-500 dark:text-neutral-400">
                      {claim.submittedDate ? format(parseISO(claim.submittedDate), 'MMM d, yyyy') : '—'}
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-neutral-900 dark:text-neutral-100">{fmt(claim.billedAmount)}</td>
                    <td className="px-6 py-4 text-right font-medium text-green-600 dark:text-green-400">{fmt(claim.paidAmount)}</td>
                    <td className="px-6 py-4">
                      <Badge label={claim.status} variant={STATUS_VARIANTS[claim.status] || 'default'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-neutral-600 dark:text-neutral-400">
              <span>
                {(pagination.page - 1) * pagination.limit + 1}–
                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
              </span>
              <div className="flex gap-2">
                <button onClick={() => setPage((p) => p - 1)} disabled={page === 1} className="btn btn-secondary disabled:opacity-40">Previous</button>
                <button onClick={() => setPage((p) => p + 1)} disabled={page === pagination.pages} className="btn btn-secondary disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
